import React, { useState } from 'react';
import classNames from 'classnames';
import Error from './error_message';
import Input from './input';
import Checkbox from './checkbox';
import Dropdown from './dropdown';
import useGym from 'helpers/use_gym';
import { useAngularService } from 'helpers/get_angular_service';

interface FailureFeeFormProps {
  data: any;
  errors: any;
  membershipPage?: boolean;
  updateData: (data: any) => void;
}

const FailureFeeForm = ({ data, errors, membershipPage, updateData }: FailureFeeFormProps) => {
  const gym = useGym();
  const ReactModal = useAngularService('ReactModal');
  const hasPaymentProvider = gym.has_payment_provider;
  const currency = gym.currency_symbol;
  const [failureFeeEnabled, setFailureFeeEnabled] = useState(data.failure_fee_amount > 0);

  const toggle = (enabled: boolean) => {
    setFailureFeeEnabled(enabled);
    if (!enabled) {
      const updated = {
        failure_fee_amount: 0,
        failure_fee_schedule_strategy: 'bundle_failure_fee_with_next_payment',
        failure_fee_invoice_email_status: 'use_membership_invoice_email_setting',
        failure_fee_auto_reschedule_status: 'use_membership_auto_reschedule_setting',
      };

      updateData(updated);
    }
  };

  const failureFeeInvoiceEmailOptions = [
    ['use_membership_invoice_email_setting', 'Use membership setting'],
    ['skip_invoice_email_for_failure_fee', 'Invoice email disabled'],
  ];

  const failureAutoRescheduleOptions = [
    ['use_membership_auto_reschedule_setting', 'Use membership setting'],
    ['skip_auto_reschedule_for_failure_fee', 'Auto-reschedule disabled'],
  ];

  const failureFeeLeadDaysValid = () => {
    return data.failure_fee_lead_days >= 1 && data.failure_fee_lead_days <= 30;
  };

  const displayFailureFeeDescription = (e) => {
    e.preventDefault();
    ReactModal.open({ component: 'FailureFeeDescriptionModal' });
  };

  if (!hasPaymentProvider) return null;

  return (
    <div>
      {!membershipPage && (
        <div>
          <h4 className="light first-one">Failure fees</h4>
        </div>
      )}

      <div className="clearfix">
        {membershipPage && <label className="control-label col-sm-3 col-lg-2">Failure fees</label>}

        <div>
          <Checkbox checked={failureFeeEnabled} onChange={(enabled) => toggle(enabled)}>
            Charge fee for bank / credit card failures?{' '}
            <i
              className="hint hint--clickable icon-question"
              onClick={displayFailureFeeDescription}
            ></i>
          </Checkbox>
          {failureFeeEnabled && (
            <div>
              <div className="clearfix form-group inline-compact margin-top-8">
                <div className="inline-module">Charge a fee of</div>
                <div className="inline-module no-margin">
                  <div className="input-group is-xs group-with-addon">
                    <span className="input-group-addon">{currency}</span>
                    <Input
                      type="number"
                      value={data.failure_fee_amount}
                      min={0}
                      onChange={(failure_fee_amount) => updateData({ failure_fee_amount })}
                    />
                  </div>
                </div>
                <div className="inline-module">for payments failed by the payment provider</div>
                <Error error={errors.failure_fee_amount} />
              </div>
              <div className="clearfix form-group">
                <label className="control-label">Schedule the fee:</label>
                <div className="btn-group btn-group--vertical inline-compact" data-toggle="buttons">
                  <button
                    type="button"
                    onClick={() =>
                      updateData({
                        failure_fee_schedule_strategy: 'bundle_failure_fee_with_next_payment',
                      })
                    }
                    className={classNames('btn btn-radio', {
                      active:
                        data.failure_fee_schedule_strategy ===
                        'bundle_failure_fee_with_next_payment',
                    })}
                  >
                    <div className="inline-module">
                      On the day of the customer's next payment, or in
                    </div>
                    <div className="inline-module">
                      <Input
                        type="number"
                        size="xs"
                        min={1}
                        value={data.failure_fee_lead_days}
                        onChange={(failure_fee_lead_days) => updateData({ failure_fee_lead_days })}
                      />
                    </div>
                    <div className="inline-module">days if no next payment exists</div>
                  </button>
                  {data.failure_fee_schedule_strategy === 'bundle_failure_fee_with_next_payment' &&
                  !failureFeeLeadDaysValid() ? (
                    <Error error={errors.failure_fee_lead_days} />
                  ) : null}
                  <br />
                  <button
                    type="button"
                    onClick={() =>
                      updateData({ failure_fee_schedule_strategy: 'use_failure_fee_lead_days' })
                    }
                    className={classNames('btn btn-radio', {
                      active: data.failure_fee_schedule_strategy === 'use_failure_fee_lead_days',
                    })}
                  >
                    <div className="inline-module">
                      <Input
                        type="number"
                        size="xs"
                        min={1}
                        value={data.failure_fee_lead_days}
                        onChange={(failure_fee_lead_days) => updateData({ failure_fee_lead_days })}
                      />
                    </div>
                    <div className="inline-module">days after the payment fails</div>
                  </button>
                  {data.failure_fee_schedule_strategy === 'use_failure_fee_lead_days' &&
                  !failureFeeLeadDaysValid() ? (
                    <Error error={errors.failure_fee_lead_days} />
                  ) : null}
                </div>
              </div>
              <div className="clearfix form-group">
                <label className="control-label margin-right-8">
                  Send an invoice email for failure fees:
                </label>
                <Dropdown
                  value={data.failure_fee_invoice_email_status}
                  options={failureFeeInvoiceEmailOptions.map((item) => ({
                    value: item[0],
                    label: item[1],
                  }))}
                  onSelect={(failure_fee_invoice_email_status) =>
                    updateData({ failure_fee_invoice_email_status })
                  }
                />
              </div>
              <div className="margin-bottom-16">
                <label className="control-label margin-right-8">
                  Auto-reschedule failure fees:
                </label>
                <Dropdown
                  value={data.failure_fee_auto_reschedule_status}
                  options={failureAutoRescheduleOptions.map((item) => ({
                    value: item[0],
                    label: item[1],
                  }))}
                  onSelect={(failure_fee_auto_reschedule_status) =>
                    updateData({ failure_fee_auto_reschedule_status })
                  }
                />
              </div>
              <div>
                <Checkbox
                  checked={data.set_failure_fees_as_tax_exempt}
                  onChange={(set_failure_fees_as_tax_exempt) =>
                    updateData({ set_failure_fees_as_tax_exempt })
                  }
                >
                  Set failure fees as tax-exempt? (overrides membership setting)
                </Checkbox>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FailureFeeForm;
